import {
  ClientOnly,
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";
import { MotionConfig } from "motion/react";

import faviconUrl from "@/web/assets/withus/favicon.png?url";
import { clerkFrontendApiOrigin } from "@/web/auth/clerkFrontendApi";
import { ClientProviders } from "@/web/components/ClientProviders";
import { ErrorFallback } from "@/web/components/styled/ErrorFallback";
import type { RouterAppContext } from "@/web/router";

import appCss from "@/web/global.css?url";

// Derived once: the publishable key is baked in at build time.
const clerkOrigin = clerkFrontendApiOrigin();

export const Route = createRootRouteWithContext<RouterAppContext>()({
  // Root must render server-side so any child route with `ssr: true` (currently
  // /ttw/$slug) can actually SSR. load-matches.ts propagates `parent.ssr === false`
  // down to all children unconditionally, so leaving the root at the router's
  // `defaultSsr: false` would block per-route SSR opt-ins.
  ssr: true,
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "WITH.CO" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: faviconUrl },
      // Warm DNS + TLS to Clerk's frontend API. `clerk.browser.js` is only
      // requested after hydration, so without this the connection is opened
      // cold and late — and the `@6` URL costs an extra uncacheable redirect
      // before the real file. `loadClerkJsScript` gives the whole sequence 15s
      // before it gives up with `failed_to_load_clerk_js`, which is the error
      // real users on slow mobile connections were hitting (TYCHO-19/1B).
      // `crossOrigin` is required: Clerk loads the script with
      // `crossOrigin: "anonymous"`, and a preconnect whose CORS mode does not
      // match the eventual request is simply discarded.
      ...(clerkOrigin
        ? [{ rel: "preconnect", href: clerkOrigin, crossOrigin: "anonymous" as const }]
        : []),
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap",
      },
      { rel: "stylesheet", href: "https://ik.imagekit.io/withco/styles/ploutos-fonts.css" },
    ],
  }),
  errorComponent: ErrorFallback,
  shellComponent: RootDocument,
  component: RootComponent,
});

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        {/*
          Static fallback title emitted before <HeadContent />. Route `head()`
          definitions still override it after hydration; this just guarantees a
          sensible title for engines/scrapers that read the first static <title>
          and don't pick up React's streamed/hydrated head (e.g. QtWebEngine).
        */}
        <title>WITH.CO</title>
        <HeadContent />
      </head>
      <body>
        <div id="root">{children}</div>
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  // ClientProviders mounts Clerk + SpacetimeDB providers — both rely on browser
  // APIs / pending Suspense and would hang or throw during SSR. Wrap in
  // ClientOnly so SSR renders <Outlet /> directly; child routes with `ssr: true`
  // (e.g. /ttw/$slug) must therefore not depend on those providers server-side
  // (they use createServerFn instead). Client takes over after hydration.
  return (
    <MotionConfig reducedMotion="user">
      <ClientOnly fallback={<Outlet />}>
        <ClientProviders>
          <Outlet />
        </ClientProviders>
      </ClientOnly>
    </MotionConfig>
  );
}
